/**
 * Driver Wallet Page — V2
 * - Overview with balance & stats
 * - Missions: bulk select + validate for payout
 * - Factures de services + Factures de commissions tabs
 * - Transactions history
 * - Payouts history
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Wallet, ArrowUpCircle, ArrowDownCircle, Euro, Clock,
  CheckCircle, XCircle, FileText, Loader2, AlertTriangle,
  List, Receipt, ChevronLeft, Banknote, Send, Download,
  CheckSquare, Square, CreditCard, Landmark, Upload, Camera, Plus
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import config from 'config';

const API_BASE = config.API_URL;

const fmtCurrency = (v) => {
  const n = parseFloat(v) || 0;
  return n.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return '—'; }
};

const fmtShortDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return '—'; }
};

const statusBadge = (status) => {
  const map = {
    PENDING: { label: 'En attente', cls: 'bg-amber-500/20 text-amber-400' },
    APPROVED: { label: 'Approuvé', cls: 'bg-emerald-500/20 text-emerald-400' },
    REJECTED: { label: 'Rejeté', cls: 'bg-red-500/20 text-red-400' },
    PAID: { label: 'Payé', cls: 'bg-blue-500/20 text-blue-400' },
  };
  const s = map[status] || { label: status, cls: 'bg-gray-500/20 text-gray-400' };
  return <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${s.cls}`}>{s.label}</span>;
};

const txTypeBadge = (type) => {
  const map = {
    MISSION_CREDIT: { label: 'Crédit mission', cls: 'text-emerald-400', icon: ArrowUpCircle },
    CREDIT_COURSE: { label: 'Crédit course', cls: 'text-emerald-400', icon: ArrowUpCircle },
    CREDIT_ATTENTE: { label: 'Attente mission', cls: 'text-amber-400', icon: Clock },
    DEBIT_COMMISSION: { label: 'Commission', cls: 'text-red-400', icon: CreditCard },
    PAYOUT_REQUEST: { label: 'Demande versement', cls: 'text-amber-400', icon: Clock },
    PAYOUT_APPROVED: { label: 'Versement effectué', cls: 'text-blue-400', icon: ArrowDownCircle },
    PAYOUT_REJECTED: { label: 'Versement rejeté', cls: 'text-red-400', icon: XCircle },
    CASH_COLLECTION: { label: 'Encaissement espèces', cls: 'text-orange-400', icon: Banknote },
    CASH_RETURNED: { label: 'Espèces remises', cls: 'text-emerald-400', icon: Send },
    ADMIN_ADJUSTMENT: { label: 'Ajustement admin', cls: 'text-purple-400', icon: Euro },
  };
  const t = map[type] || { label: type, cls: 'text-gray-400', icon: List };
  const Icon = t.icon;
  return <span className={`flex items-center gap-1 text-xs ${t.cls}`}><Icon className="w-3.5 h-3.5" />{t.label}</span>;
};

// Amount display: color + sign based on transaction type
const txAmountDisplay = (tx) => {
  const amount = parseFloat(tx.amount) || 0;
  const type = tx.type;

  // PAYOUT_REQUEST = pending, neutral orange, no sign
  if (type === 'PAYOUT_REQUEST') {
    return <span className="text-sm font-semibold text-amber-400">{fmtCurrency(Math.abs(amount))}</span>;
  }
  // PAYOUT_APPROVED = debit, blue, negative
  if (type === 'PAYOUT_APPROVED') {
    return <span className="text-sm font-semibold text-blue-400">-{fmtCurrency(Math.abs(amount))}</span>;
  }
  // DEBIT_COMMISSION = red, negative
  if (type === 'DEBIT_COMMISSION') {
    return <span className="text-sm font-semibold text-red-400">-{fmtCurrency(Math.abs(amount))}</span>;
  }
  // Credits = green, positive
  if (amount >= 0) {
    return <span className="text-sm font-semibold text-emerald-400">+{fmtCurrency(amount)}</span>;
  }
  // Other negatives = red
  return <span className="text-sm font-semibold text-red-400">{fmtCurrency(amount)}</span>;
};

const fmtMonth = (ym) => {
  try {
    const [y, m] = ym.split('-');
    const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
    return `${months[parseInt(m, 10) - 1]} ${y}`;
  } catch { return ym; }
};

function FiscalSummaryTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const getToken = () => localStorage.getItem('driver_access_token') || localStorage.getItem('access_token') || localStorage.getItem('token');

  useEffect(() => {
    const fetchFiscal = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/wallet/fiscal-summary`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        const d = await res.json();
        setData(d);
      } catch { /* silent */ }
      setLoading(false);
    };
    fetchFiscal();
  }, []);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-[#C9A24D] animate-spin" /></div>;
  if (!data || !data.months?.length) return (
    <div className="text-center py-12" data-testid="fiscal-empty">
      <Euro className="w-10 h-10 text-[#C9A24D]/20 mx-auto mb-2" />
      <p className="text-[#E5E5E5]/30 text-sm">Aucune donnée fiscale disponible</p>
    </div>
  );

  const { months, totals, is_vat_applicable, vat_rate, commission_rate } = data;

  return (
    <div className="space-y-4" data-testid="fiscal-summary">
      {/* Fiscal config info */}
      <div className="bg-[#1F1F1F] rounded-xl p-3 border border-[#C9A24D]/20 flex gap-4 text-[10px]">
        <span className="text-[#E5E5E5]/50">TVA: <strong className={is_vat_applicable ? 'text-emerald-400' : 'text-[#E5E5E5]/30'}>{is_vat_applicable ? `Assujetti (${(vat_rate * 100).toFixed(0)}%)` : 'Non assujetti'}</strong></span>
        {commission_rate > 0 && <span className="text-[#E5E5E5]/50">Commission: <strong className="text-orange-400">{(commission_rate * 100).toFixed(0)}%</strong></span>}
      </div>

      {/* Grand totals */}
      <div className="bg-gradient-to-br from-[#C9A24D]/15 to-[#0B0B0B] rounded-xl p-4 border border-[#C9A24D]/30">
        <p className="text-[#E5E5E5]/40 text-[10px] mb-2 uppercase tracking-wider">Totaux cumulés</p>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <p className="text-[#E5E5E5]/40 text-[9px]">Chiffre d'affaires HT</p>
            <p className="text-white text-sm font-bold" data-testid="fiscal-total-ht">{fmtCurrency(totals.total_ht)}</p>
          </div>
          {is_vat_applicable && (
            <div>
              <p className="text-[#E5E5E5]/40 text-[9px]">TVA collectée</p>
              <p className="text-blue-400 text-sm font-semibold" data-testid="fiscal-total-tva">{fmtCurrency(totals.total_tva)}</p>
            </div>
          )}
          <div>
            <p className="text-[#E5E5E5]/40 text-[9px]">Total TTC</p>
            <p className="text-emerald-400 text-sm font-semibold" data-testid="fiscal-total-ttc">{fmtCurrency(totals.total_ttc)}</p>
          </div>
        </div>
        {commission_rate > 0 && (
          <div className="mt-3 pt-3 border-t border-[#C9A24D]/10 grid grid-cols-3 gap-3">
            <div>
              <p className="text-[#E5E5E5]/40 text-[9px]">Commissions TTC</p>
              <p className="text-orange-400 text-sm font-semibold">-{fmtCurrency(totals.commissions_ttc)}</p>
            </div>
            <div>
              <p className="text-[#E5E5E5]/40 text-[9px]">Net TTC</p>
              <p className="text-[#C9A24D] text-sm font-bold">{fmtCurrency(totals.net_ttc)}</p>
            </div>
            <div>
              <p className="text-[#E5E5E5]/40 text-[9px]">Versé</p>
              <p className="text-blue-400 text-sm font-semibold">{fmtCurrency(totals.paid_out)}</p>
            </div>
          </div>
        )}
      </div>

      {/* Monthly breakdown */}
      <div>
        <h3 className="text-white font-semibold text-sm mb-2">Détail mensuel</h3>
        <div className="space-y-2">
          {months.map(m => (
            <div key={m.month} className="bg-[#1A1A1A] rounded-xl p-3 border border-transparent hover:border-[#C9A24D]/10" data-testid={`fiscal-month-${m.month}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[#C9A24D] text-xs font-semibold">{fmtMonth(m.month)}</span>
                <span className="text-[#E5E5E5]/30 text-[10px]">{m.mission_count} mission{m.mission_count > 1 ? 's' : ''}</span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-[10px]">
                <div>
                  <p className="text-[#E5E5E5]/40">CA HT</p>
                  <p className="text-white font-semibold">{fmtCurrency(m.total_ht)}</p>
                </div>
                {is_vat_applicable && (
                  <div>
                    <p className="text-[#E5E5E5]/40">TVA</p>
                    <p className="text-blue-400 font-semibold">{fmtCurrency(m.total_tva)}</p>
                  </div>
                )}
                <div>
                  <p className="text-[#E5E5E5]/40">TTC</p>
                  <p className="text-emerald-400 font-semibold">{fmtCurrency(m.total_ttc)}</p>
                </div>
              </div>
              {commission_rate > 0 && (
                <div className="grid grid-cols-3 gap-2 text-[10px] mt-1.5 pt-1.5 border-t border-[#333]">
                  <div>
                    <p className="text-[#E5E5E5]/40">Commission</p>
                    <p className="text-orange-400 font-semibold">-{fmtCurrency(m.commissions_ttc)}</p>
                  </div>
                  <div>
                    <p className="text-[#E5E5E5]/40">Net</p>
                    <p className="text-[#C9A24D] font-semibold">{fmtCurrency(m.net_ttc)}</p>
                  </div>
                  <div>
                    <p className="text-[#E5E5E5]/40">Versé</p>
                    <p className="text-blue-400 font-semibold">{fmtCurrency(m.paid_out)}</p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function WalletPage() {
  const navigate = useNavigate();
  const basePath = window.location.pathname.startsWith('/chauffeur') ? '/chauffeur' : '/app';
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [missions, setMissions] = useState([]);
  const [payoutRequests, setPayoutRequests] = useState([]);
  const [servicesInvoices, setServicesInvoices] = useState([]);
  const [commissionsInvoices, setCommissionsInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [accessError, setAccessError] = useState(false);
  const [selectedMissions, setSelectedMissions] = useState(new Set());
  const [showCashForm, setShowCashForm] = useState(false);
  const [showRemitForm, setShowRemitForm] = useState(false);
  const [cashAmount, setCashAmount] = useState('');
  const [cashReason, setCashReason] = useState('');
  const [remitAmount, setRemitAmount] = useState('');
  const [bankInfo, setBankInfo] = useState({ account_holder: '', iban: '', bic: '' });
  const [bankForm, setBankForm] = useState({ account_holder: '', iban: '', bic: '' });
  const [bankSaving, setBankSaving] = useState(false);
  const [bankSuccess, setBankSuccess] = useState('');

  // Transaction filters (F2)
  const [txPeriod, setTxPeriod] = useState('30');
  const [txType, setTxType] = useState('all');

  // Expense tracking (internal drivers)
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [expType, setExpType] = useState('parking');
  const [expAmount, setExpAmount] = useState('');
  const [expDesc, setExpDesc] = useState('');
  const [expReceipt, setExpReceipt] = useState(null);
  const [expSubmitting, setExpSubmitting] = useState(false);
  const [recentExpenses, setRecentExpenses] = useState([]);

  const getToken = () => localStorage.getItem('driver_access_token') || localStorage.getItem('access_token') || localStorage.getItem('token');
  const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });
  const isFleetDriver = wallet?.is_fleet_driver;
  const isInternalDriver = wallet?.fleet_status === 'interne';
  const canRemitCash = isFleetDriver && !isInternalDriver;

  const fetchData = useCallback(async () => {
    try {
      const headers = authHeaders();
      const [walletRes, txRes] = await Promise.all([
        fetch(`${API_BASE}/api/wallet`, { headers }).then(r => r.json()),
        fetch(`${API_BASE}/api/wallet/transactions?limit=100`, { headers }).then(r => r.json()),
      ]);
      if (walletRes.detail) { setAccessError(true); return; }
      setWallet(walletRes.wallet);
      setTransactions(txRes.transactions || []);

      if (!walletRes.wallet?.is_fleet_driver) {
        const [missionsRes, payoutsRes, svcInvRes, comInvRes, bankRes] = await Promise.all([
          fetch(`${API_BASE}/api/wallet/missions`, { headers }).then(r => r.json()),
          fetch(`${API_BASE}/api/wallet/payout-requests`, { headers }).then(r => r.json()),
          fetch(`${API_BASE}/api/wallet/invoices?invoice_type=services`, { headers }).then(r => r.json()),
          fetch(`${API_BASE}/api/wallet/invoices?invoice_type=commissions`, { headers }).then(r => r.json()),
          fetch(`${API_BASE}/api/wallet/bank-info`, { headers }).then(r => r.json()),
        ]);
        setMissions(missionsRes.missions || []);
        setPayoutRequests(payoutsRes.payout_requests || []);
        setServicesInvoices(svcInvRes.invoices || []);
        setCommissionsInvoices(comInvRes.invoices || []);
        if (bankRes.account_holder !== undefined) {
          setBankInfo(bankRes);
          setBankForm(bankRes);
        }
      }
    } catch (err) {
      console.error('Wallet fetch error:', err);
      setAccessError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Fetch recent expenses for internal drivers
  useEffect(() => {
    if (!isInternalDriver) return;
    const fetchExpenses = async () => {
      try {
        const headers = authHeaders();
        const res = await fetch(`${API_BASE}/api/chauffeur/shifts/history`, { headers });
        const data = await res.json();
        const allExpenses = (data.shifts || []).flatMap(s =>
          (s.expenses || []).map(e => ({ ...e, shift_date: s.date || s.started_at }))
        );
        setRecentExpenses(allExpenses.slice(0, 10));
      } catch { /* silent */ }
    };
    fetchExpenses();
  }, [isInternalDriver]);

  const handleExpenseSubmit = async (e) => {
    e.preventDefault();
    if (!expAmount || parseFloat(expAmount) <= 0) { setError('Montant invalide'); return; }
    setExpSubmitting(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('expense_type', expType);
      formData.append('amount', parseFloat(expAmount));
      formData.append('description', expDesc);
      if (expReceipt) formData.append('receipt', expReceipt);
      const res = await fetch(`${API_BASE}/api/chauffeur/shifts/current/expense`, {
        method: 'POST',
        headers: authHeaders(),
        body: formData,
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || 'Erreur'); }
      setShowExpenseForm(false);
      setExpAmount(''); setExpDesc(''); setExpReceipt(null); setExpType('parking');
      // Re-fetch to update history
      fetchData();
    } catch (err) { setError(err.message); }
    setExpSubmitting(false);
  };

  const EXPENSE_TYPES = [
    { value: 'parking', label: 'Parking' },
    { value: 'pharmacie', label: 'Pharmacie' },
    { value: 'repas_client', label: 'Repas client' },
    { value: 'autre', label: 'Autre' },
  ];

  // Bulk selection helpers
  const unbilledMissions = missions.filter(m => !m.is_invoiced);
  const toggleMission = (id) => {
    setSelectedMissions(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selectedMissions.size === unbilledMissions.length) {
      setSelectedMissions(new Set());
    } else {
      setSelectedMissions(new Set(unbilledMissions.map(m => m.mission_id)));
    }
  };

  const selectedTotal = missions
    .filter(m => selectedMissions.has(m.mission_id))
    .reduce((sum, m) => sum + (m.amount || 0), 0);
  const selectedCommissions = missions
    .filter(m => selectedMissions.has(m.mission_id))
    .reduce((sum, m) => sum + (m.commission_amount || 0), 0);
  const selectedNet = selectedTotal - selectedCommissions;

  const handlePayoutSubmit = async () => {
    if (selectedMissions.size === 0) return;
    if (!bankInfo.iban) {
      setError('Veuillez enregistrer vos informations bancaires (IBAN) avant de demander un versement');
      setActiveTab('iban');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/wallet/payout-request-v2`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ mission_ids: Array.from(selectedMissions) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erreur');
      setSelectedMissions(new Set());
      fetchData();
    } catch (err) { setError(err.message); }
    setSubmitting(false);
  };

  const handleCashSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const amount = parseFloat(cashAmount);
    if (!amount || amount <= 0) { setError('Montant invalide'); return; }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('amount', amount);
      formData.append('reason', cashReason || 'Encaissement espèces');
      const res = await fetch(`${API_BASE}/api/wallet/cash-collection`, {
        method: 'POST', headers: authHeaders(), body: formData,
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || 'Erreur'); }
      setShowCashForm(false); setCashAmount(''); setCashReason(''); fetchData();
    } catch (err) { setError(err.message); }
    setSubmitting(false);
  };

  const handleRemitSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const amount = parseFloat(remitAmount);
    if (!amount || amount <= 0) { setError('Montant invalide'); return; }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('amount', amount);
      const res = await fetch(`${API_BASE}/api/wallet/cash-remit`, {
        method: 'POST', headers: authHeaders(), body: formData,
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || 'Erreur'); }
      setShowRemitForm(false); setRemitAmount(''); fetchData();
    } catch (err) { setError(err.message); }
    setSubmitting(false);
  };

  const handleBankSave = async (e) => {
    e.preventDefault();
    setBankSuccess('');
    setError('');
    if (!bankForm.account_holder.trim()) { setError('Le nom du titulaire est requis'); return; }
    if (!bankForm.iban.trim()) { setError("L'IBAN est requis"); return; }
    if (!bankForm.bic.trim()) { setError('Le BIC est requis'); return; }
    setBankSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/wallet/bank-info`, {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(bankForm),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erreur');
      setBankInfo({ ...bankForm, iban: bankForm.iban.replace(/\s/g, '').toUpperCase() });
      setBankSuccess('Informations bancaires enregistrées');
    } catch (err) { setError(err.message); }
    setBankSaving(false);
  };

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 text-[#C9A24D] animate-spin" /></div>;
  if (accessError) return (
    <div className="p-4 text-center">
      <AlertTriangle className="w-10 h-10 text-amber-400 mx-auto mb-3" />
      <p className="text-[#E5E5E5]/60 text-sm">Accès au wallet non disponible pour votre profil.</p>
    </div>
  );

  const cashHeld = Math.abs(wallet?.balance || 0);

  // ========= FLEET DRIVER: Simplified cash-only view =========
  if (isFleetDriver) {
    return (
      <div className="min-h-[calc(100vh-140px)] p-4 space-y-4" data-testid="driver-wallet-page">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(basePath)} className="text-[#E5E5E5]/40 hover:text-white"><ChevronLeft className="w-5 h-5" /></button>
          <div>
            <h1 className="text-white font-bold text-lg flex items-center gap-2"><Banknote className="w-5 h-5 text-orange-400" /> Espèces</h1>
            <p className="text-[#E5E5E5]/30 text-xs">{wallet?.owner_name}</p>
          </div>
        </div>
        <div className="bg-gradient-to-br from-orange-500/20 to-[#0B0B0B] rounded-xl p-5 border border-orange-500/30">
          <p className="text-[#E5E5E5]/40 text-xs mb-1">Espèces en votre possession</p>
          <p className="text-3xl font-bold text-orange-400" data-testid="fleet-driver-cash-balance">{fmtCurrency(cashHeld)}</p>
        </div>
        <div className={canRemitCash ? "grid grid-cols-2 gap-3" : ""}>
          {!showCashForm && !showRemitForm && (<>
            <button onClick={() => { setShowCashForm(true); setShowRemitForm(false); setError(''); }}
              className="w-full bg-orange-500/15 text-orange-400 border border-orange-500/20 rounded-xl py-3.5 font-semibold text-sm flex items-center justify-center gap-2" data-testid="fleet-cash-collect-btn">
              <Banknote className="w-4 h-4" /> Encaisser
            </button>
            {canRemitCash && (
            <button onClick={() => { setShowRemitForm(true); setShowCashForm(false); setError(''); }} disabled={cashHeld <= 0}
              className="bg-emerald-500/15 text-emerald-400 border border-emerald-500/20 rounded-xl py-3.5 font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-30" data-testid="fleet-cash-remit-btn">
              <Send className="w-4 h-4" /> Remettre
            </button>
            )}
          </>)}
        </div>
        {showCashForm && (
          <div className="bg-[#1F1F1F] rounded-xl p-4 border border-orange-500/30">
            <form onSubmit={handleCashSubmit} className="space-y-3">
              <input type="number" step="0.01" min="0.01" value={cashAmount} onChange={e => setCashAmount(e.target.value)}
                className="w-full bg-[#2A2A2A] border border-orange-500/20 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-orange-400" placeholder="Montant (EUR)" />
              <input type="text" value={cashReason} onChange={e => setCashReason(e.target.value)}
                className="w-full bg-[#2A2A2A] border border-orange-500/20 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-orange-400" placeholder="Raison" />
              {error && <p className="text-red-400 text-xs">{error}</p>}
              <div className="flex gap-2">
                <button type="submit" disabled={submitting} className="flex-1 bg-orange-500 text-white rounded-xl py-3 font-semibold text-sm">{submitting ? 'Envoi...' : 'Enregistrer'}</button>
                <button type="button" onClick={() => setShowCashForm(false)} className="px-4 rounded-xl border border-[#333] text-[#E5E5E5]/50 text-sm">Annuler</button>
              </div>
            </form>
          </div>
        )}
        {showRemitForm && (
          <div className="bg-[#1F1F1F] rounded-xl p-4 border border-emerald-500/30">
            <form onSubmit={handleRemitSubmit} className="space-y-3">
              <input type="number" step="0.01" min="0.01" max={cashHeld} value={remitAmount} onChange={e => setRemitAmount(e.target.value)}
                className="w-full bg-[#2A2A2A] border border-emerald-500/20 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-emerald-400" placeholder={`Max: ${fmtCurrency(cashHeld)}`} />
              {error && <p className="text-red-400 text-xs">{error}</p>}
              <div className="flex gap-2">
                <button type="submit" disabled={submitting} className="flex-1 bg-emerald-600 text-white rounded-xl py-3 font-semibold text-sm">{submitting ? 'Envoi...' : 'Confirmer'}</button>
                <button type="button" onClick={() => setShowRemitForm(false)} className="px-4 rounded-xl border border-[#333] text-[#E5E5E5]/50 text-sm">Annuler</button>
              </div>
            </form>
          </div>
        )}
        {/* ─── Expenses Section (Internal Drivers) ─── */}
        {isInternalDriver && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-white font-semibold text-sm flex items-center gap-2">
                <Receipt className="w-4 h-4 text-[#C9A24D]" /> Notes de frais
              </h3>
              {!showExpenseForm && (
                <button onClick={() => { setShowExpenseForm(true); setError(''); }}
                  className="text-[#C9A24D] text-xs flex items-center gap-1 hover:text-[#E6C77A]"
                  data-testid="add-expense-btn">
                  <Plus className="w-3.5 h-3.5" /> Ajouter
                </button>
              )}
            </div>

            {showExpenseForm && (
              <div className="bg-[#1F1F1F] rounded-xl p-4 border border-[#C9A24D]/30 mb-3">
                <form onSubmit={handleExpenseSubmit} className="space-y-3">
                  <select value={expType} onChange={e => setExpType(e.target.value)}
                    className="w-full bg-[#2A2A2A] border border-[#C9A24D]/20 rounded-xl px-4 py-3 text-white text-sm focus:outline-none"
                    data-testid="expense-type-select">
                    {EXPENSE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                  <input type="number" step="0.01" min="0.01" value={expAmount} onChange={e => setExpAmount(e.target.value)}
                    className="w-full bg-[#2A2A2A] border border-[#C9A24D]/20 rounded-xl px-4 py-3 text-white text-sm focus:outline-none"
                    placeholder="Montant (EUR)" data-testid="expense-amount-input" />
                  <input type="text" value={expDesc} onChange={e => setExpDesc(e.target.value)}
                    className="w-full bg-[#2A2A2A] border border-[#C9A24D]/20 rounded-xl px-4 py-3 text-white text-sm focus:outline-none"
                    placeholder="Description (optionnel)" data-testid="expense-desc-input" />
                  <label className="flex items-center gap-2 px-4 py-3 bg-[#2A2A2A] border border-[#C9A24D]/20 rounded-xl cursor-pointer hover:border-[#C9A24D]/40 transition">
                    {expReceipt ? (
                      <><CheckCircle className="w-4 h-4 text-emerald-400" /><span className="text-emerald-400 text-sm truncate">{expReceipt.name}</span></>
                    ) : (
                      <><Upload className="w-4 h-4 text-[#E5E5E5]/40" /><span className="text-[#E5E5E5]/40 text-sm">Joindre un recu</span></>
                    )}
                    <input type="file" accept="image/*,.pdf" className="hidden"
                      onChange={e => setExpReceipt(e.target.files?.[0] || null)} data-testid="expense-receipt-input" />
                  </label>
                  {error && <p className="text-red-400 text-xs">{error}</p>}
                  <div className="flex gap-2">
                    <button type="submit" disabled={expSubmitting || !expAmount}
                      className="flex-1 bg-[#C9A24D] text-[#0B0B0B] rounded-xl py-3 font-semibold text-sm"
                      data-testid="submit-expense-btn">
                      {expSubmitting ? 'Envoi...' : 'Enregistrer'}
                    </button>
                    <button type="button" onClick={() => { setShowExpenseForm(false); setError(''); }}
                      className="px-4 rounded-xl border border-[#333] text-[#E5E5E5]/50 text-sm">Annuler</button>
                  </div>
                </form>
              </div>
            )}

            {recentExpenses.length > 0 ? (
              <div className="space-y-1.5 mb-4">
                {recentExpenses.map((exp, i) => (
                  <div key={i} className="flex items-center justify-between bg-[#1A1A1A] rounded-xl px-3 py-2.5">
                    <div>
                      <span className="text-xs text-[#C9A24D]">{exp.type || exp.expense_type || '--'}</span>
                      {exp.description && <p className="text-[#E5E5E5]/30 text-[10px]">{exp.description}</p>}
                      <p className="text-[#E5E5E5]/20 text-[10px]">{exp.shift_date ? fmtDate(exp.shift_date) : '--'}</p>
                    </div>
                    <span className="text-white text-sm font-semibold">{fmtCurrency(exp.amount)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-white/30 text-sm text-center py-4 mb-4">Aucune note de frais</p>
            )}
          </div>
        )}

        <div>
          <h3 className="text-white font-semibold text-sm mb-2">Historique</h3>
          {transactions.length === 0 ? <p className="text-white/30 text-sm text-center py-8">Aucune transaction</p> :
            <div className="space-y-1.5" data-testid="fleet-driver-transactions">
              {transactions.map(tx => (
                <div key={tx.id} className="flex items-center justify-between bg-[#1A1A1A] rounded-xl px-3 py-2.5">
                  <div>{txTypeBadge(tx.type)}<p className="text-[#E5E5E5]/20 text-[10px] mt-0.5">{fmtDate(tx.created_at)}</p></div>
                  {txAmountDisplay(tx)}
                </div>
              ))}
            </div>
          }
        </div>
      </div>
    );
  }

  // ========= INDEPENDENT: Full wallet view =========
  const tabs = [
    { key: 'overview', label: 'Apercu', icon: Wallet },
    { key: 'missions', label: 'Missions', icon: Receipt },
    { key: 'fiscal', label: 'Fiscal', icon: Euro },
    { key: 'iban', label: 'IBAN', icon: Landmark },
    { key: 'invoices-services', label: 'Fact. services', icon: FileText },
    { key: 'invoices-commissions', label: 'Fact. commissions', icon: CreditCard },
    { key: 'transactions', label: 'Historique', icon: List },
    { key: 'payouts', label: 'Versements', icon: ArrowDownCircle },
  ];

  return (
    <div className="min-h-[calc(100vh-140px)] p-4 space-y-4" data-testid="driver-wallet-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(basePath)} className="text-[#E5E5E5]/40 hover:text-white"><ChevronLeft className="w-5 h-5" /></button>
        <div>
          <h1 className="text-white font-bold text-lg flex items-center gap-2"><Wallet className="w-5 h-5 text-[#C9A24D]" /> Mon Wallet</h1>
          <p className="text-[#E5E5E5]/30 text-xs">{wallet?.owner_name}</p>
        </div>
      </div>

      {/* Tabs — scrollable */}
      <div className="flex gap-1 bg-[#1A1A1A] rounded-lg p-1 overflow-x-auto no-scrollbar">
        {tabs.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.key} onClick={() => setActiveTab(t.key)}
              className={`flex items-center justify-center gap-1 px-3 py-2 rounded-md text-[11px] font-medium transition-all whitespace-nowrap flex-shrink-0
                ${activeTab === t.key ? 'bg-[#C9A24D] text-[#0B0B0B]' : 'text-[#E5E5E5]/50 hover:text-[#E5E5E5]/80'}`}
              data-testid={`driver-wallet-tab-${t.key}`}>
              <Icon className="w-3.5 h-3.5" />{t.label}
            </button>
          );
        })}
      </div>

      {/* ── Overview ── */}
      {activeTab === 'overview' && wallet && (
        <div className="space-y-4">
          <div className="bg-gradient-to-br from-[#C9A24D]/20 to-[#0B0B0B] rounded-xl p-5 border border-[#C9A24D]/30">
            <p className="text-[#E5E5E5]/40 text-xs mb-1">Solde disponible</p>
            <p className="text-3xl font-bold text-[#C9A24D]" data-testid="driver-wallet-balance">{fmtCurrency(wallet.balance)}</p>
            <div className="flex gap-4 mt-3">
              <div>
                <p className="text-[#E5E5E5]/30 text-[10px]">Total gagné</p>
                <p className="text-emerald-400 text-sm font-semibold">{fmtCurrency(wallet.total_earned)}</p>
              </div>
              <div>
                <p className="text-[#E5E5E5]/30 text-[10px]">Commissions</p>
                <p className="text-orange-400 text-sm font-semibold">-{fmtCurrency(wallet.total_commissions)}</p>
              </div>
              <div>
                <p className="text-[#E5E5E5]/30 text-[10px]">Versé</p>
                <p className="text-blue-400 text-sm font-semibold">{fmtCurrency(wallet.total_paid_out)}</p>
              </div>
            </div>
          </div>

          {/* Quick action: go to missions to request payout */}
          {unbilledMissions.length > 0 && !wallet.has_pending_payout && (
            <button onClick={() => setActiveTab('missions')}
              className="w-full bg-[#C9A24D] text-[#0B0B0B] rounded-xl py-3.5 font-semibold text-sm flex items-center justify-center gap-2 active:scale-[0.98]"
              data-testid="driver-request-payout-btn">
              <ArrowDownCircle className="w-4 h-4" /> Demander un versement ({unbilledMissions.length} missions)
            </button>
          )}
          {wallet.has_pending_payout && (
            <div className="flex items-center gap-2 p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl text-amber-400 text-sm">
              <Clock className="w-4 h-4 flex-shrink-0" /> Demande en cours de traitement
            </div>
          )}
          {!bankInfo.iban && (
            <button onClick={() => setActiveTab('iban')}
              className="w-full flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm"
              data-testid="driver-iban-missing-alert">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" /> Enregistrez votre IBAN pour pouvoir demander un versement
            </button>
          )}

          {/* Cash collect */}
          {!showCashForm && (
            <button onClick={() => setShowCashForm(true)}
              className="w-full bg-orange-500/15 text-orange-400 border border-orange-500/20 rounded-xl py-3 font-semibold text-sm flex items-center justify-center gap-2" data-testid="driver-cash-collection-btn">
              <Euro className="w-4 h-4" /> Déclarer un encaissement espèces
            </button>
          )}
          {showCashForm && (
            <div className="bg-[#1F1F1F] rounded-xl p-4 border border-orange-500/30">
              <form onSubmit={handleCashSubmit} className="space-y-3">
                <input type="number" step="0.01" min="0.01" value={cashAmount} onChange={e => setCashAmount(e.target.value)}
                  className="w-full bg-[#2A2A2A] border border-orange-500/20 rounded-xl px-4 py-3 text-white text-sm focus:outline-none" placeholder="Montant (EUR)" data-testid="driver-cash-amount" />
                <input type="text" value={cashReason} onChange={e => setCashReason(e.target.value)}
                  className="w-full bg-[#2A2A2A] border border-orange-500/20 rounded-xl px-4 py-3 text-white text-sm focus:outline-none" placeholder="Raison" />
                {error && <p className="text-red-400 text-xs">{error}</p>}
                <div className="flex gap-2">
                  <button type="submit" disabled={submitting} className="flex-1 bg-orange-500 text-white rounded-xl py-3 font-semibold text-sm">{submitting ? 'Envoi...' : 'Enregistrer'}</button>
                  <button type="button" onClick={() => { setShowCashForm(false); setError(''); }} className="px-4 rounded-xl border border-[#333] text-[#E5E5E5]/50 text-sm">Annuler</button>
                </div>
              </form>
            </div>
          )}

          {/* Recent transactions */}
          {transactions.length > 0 && (
            <div>
              <h3 className="text-white font-semibold text-sm mb-2">Dernières transactions</h3>
              <div className="space-y-1.5">
                {transactions.slice(0, 5).map(tx => (
                  <div key={tx.id} className="flex items-center justify-between bg-[#1A1A1A] rounded-xl px-3 py-2.5">
                    <div>{txTypeBadge(tx.type)}<p className="text-[#E5E5E5]/30 text-[10px] mt-0.5">{fmtDate(tx.created_at)}</p></div>
                    {txAmountDisplay(tx)}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Missions Tab (with bulk select) ── */}
      {activeTab === 'missions' && (
        <div className="space-y-3" data-testid="driver-wallet-missions">
          {/* Select all + validate */}
          {unbilledMissions.length > 0 && (
            <div className="flex items-center justify-between bg-[#1F1F1F] rounded-xl p-3 border border-[#C9A24D]/20">
              <button onClick={toggleAll} className="flex items-center gap-2 text-sm text-[#E5E5E5]/70 hover:text-white" data-testid="mission-select-all">
                {selectedMissions.size === unbilledMissions.length
                  ? <CheckSquare className="w-4 h-4 text-[#C9A24D]" />
                  : <Square className="w-4 h-4 text-[#E5E5E5]/30" />}
                Tout sélectionner ({unbilledMissions.length})
              </button>
              {selectedMissions.size > 0 && (
                <div className="text-right">
                  <p className="text-[#C9A24D] text-xs font-semibold">{selectedMissions.size} sélectionnée{selectedMissions.size > 1 ? 's' : ''}</p>
                  <p className="text-[#E5E5E5]/40 text-[10px]">Tarif: {fmtCurrency(selectedTotal)} | Com: {fmtCurrency(selectedCommissions)} | Net: {fmtCurrency(selectedNet)}</p>
                </div>
              )}
            </div>
          )}

          {/* Mission list */}
          {missions.length === 0 ? (
            <div className="text-center py-8"><Receipt className="w-10 h-10 text-[#C9A24D]/20 mx-auto mb-2" /><p className="text-[#E5E5E5]/30 text-sm">Aucune mission créditée</p></div>
          ) : missions.map(m => {
            const isSelected = selectedMissions.has(m.mission_id);
            const isDisabled = m.is_invoiced;
            return (
              <div key={m.mission_id}
                onClick={() => !isDisabled && toggleMission(m.mission_id)}
                className={`bg-[#1A1A1A] rounded-xl p-3 border transition-all cursor-pointer
                  ${isDisabled ? 'border-transparent opacity-50 cursor-default' : isSelected ? 'border-[#C9A24D]/50 bg-[#C9A24D]/5' : 'border-transparent hover:border-[#C9A24D]/20'}`}
                data-testid={`mission-row-${m.mission_id}`}>
                <div className="flex items-start gap-3">
                  {!isDisabled ? (
                    isSelected ? <CheckSquare className="w-5 h-5 text-[#C9A24D] flex-shrink-0 mt-0.5" /> : <Square className="w-5 h-5 text-[#E5E5E5]/20 flex-shrink-0 mt-0.5" />
                  ) : (
                    <CheckCircle className="w-5 h-5 text-emerald-500/40 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-[#C9A24D] text-xs font-mono">{m.reference || m.mission_id?.slice(0, 8)}</p>
                      {isDisabled && <span className="text-[10px] bg-emerald-500/15 text-emerald-400 px-2 py-0.5 rounded">Facturée</span>}
                    </div>
                    <p className="text-[#E5E5E5]/50 text-[10px]">{fmtShortDate(m.date)} — {m.client_name || '—'}</p>
                    <p className="text-[#E5E5E5]/30 text-[10px] truncate">{m.pickup_address?.split(',')[0]} → {m.dropoff_address?.split(',')[0]}</p>
                    <div className="flex items-center gap-3 mt-1.5">
                      <div>
                        <p className="text-[#E5E5E5]/30 text-[9px]">Tarif</p>
                        <p className="text-emerald-400 text-xs font-semibold">{fmtCurrency(m.course_amount || m.amount)}</p>
                      </div>
                      {m.waiting_amount > 0 && (
                        <div>
                          <p className="text-[#E5E5E5]/30 text-[9px]">Attente ({m.waiting_hours}h)</p>
                          <p className="text-amber-400 text-xs font-semibold">+{fmtCurrency(m.waiting_amount)}</p>
                        </div>
                      )}
                      {m.commission_amount > 0 && (
                        <div>
                          <p className="text-[#E5E5E5]/30 text-[9px]">Commission ({(m.commission_rate * 100).toFixed(0)}%)</p>
                          <p className="text-orange-400 text-xs font-semibold">-{fmtCurrency(m.commission_amount)}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-[#E5E5E5]/30 text-[9px]">Net</p>
                        <p className="text-white text-xs font-bold">{fmtCurrency(m.net_amount)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Validate payout button */}
          {selectedMissions.size > 0 && (
            <div className="sticky bottom-4 bg-[#0B0B0B]/95 backdrop-blur-sm p-3 rounded-xl border border-[#C9A24D]/30">
              {error && <p className="text-red-400 text-xs mb-2 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{error}</p>}
              <button onClick={handlePayoutSubmit} disabled={submitting}
                className="w-full bg-[#C9A24D] text-[#0B0B0B] rounded-xl py-3.5 font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[0.98]"
                data-testid="driver-validate-payout-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowDownCircle className="w-4 h-4" />}
                Valider pour versement ({selectedMissions.size} missions — Net: {fmtCurrency(selectedNet)})
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Fiscal Summary Tab ── */}
      {activeTab === 'fiscal' && <FiscalSummaryTab />}

      {/* ── IBAN Tab ── */}
      {activeTab === 'iban' && (
        <div className="space-y-4" data-testid="driver-wallet-iban">
          <div className="bg-[#1F1F1F] rounded-xl p-4 border border-[#C9A24D]/20">
            <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2"><Landmark className="w-4 h-4 text-[#C9A24D]" /> Informations bancaires</h3>
            <form onSubmit={handleBankSave} className="space-y-3">
              <div>
                <label className="text-[#E5E5E5]/50 text-xs mb-1 block">Nom du titulaire du compte</label>
                <input type="text" value={bankForm.account_holder} onChange={e => setBankForm(p => ({ ...p, account_holder: e.target.value }))}
                  className="w-full bg-[#2A2A2A] border border-[#333] rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#C9A24D]"
                  placeholder="Nom Prénom ou Raison sociale" data-testid="bank-account-holder" />
              </div>
              <div>
                <label className="text-[#E5E5E5]/50 text-xs mb-1 block">IBAN</label>
                <input type="text" value={bankForm.iban} onChange={e => setBankForm(p => ({ ...p, iban: e.target.value }))}
                  className="w-full bg-[#2A2A2A] border border-[#333] rounded-xl px-4 py-3 text-white text-sm font-mono tracking-wider focus:outline-none focus:border-[#C9A24D]"
                  placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX" data-testid="bank-iban" />
              </div>
              <div>
                <label className="text-[#E5E5E5]/50 text-xs mb-1 block">BIC</label>
                <input type="text" value={bankForm.bic} onChange={e => setBankForm(p => ({ ...p, bic: e.target.value }))}
                  className="w-full bg-[#2A2A2A] border border-[#333] rounded-xl px-4 py-3 text-white text-sm font-mono tracking-wider focus:outline-none focus:border-[#C9A24D]"
                  placeholder="BNPAFRPP" data-testid="bank-bic" />
              </div>
              {error && activeTab === 'iban' && <p className="text-red-400 text-xs flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{error}</p>}
              {bankSuccess && <p className="text-emerald-400 text-xs flex items-center gap-1"><CheckCircle className="w-3 h-3" />{bankSuccess}</p>}
              <button type="submit" disabled={bankSaving}
                className="w-full bg-[#C9A24D] text-[#0B0B0B] rounded-xl py-3 font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="bank-save-btn">
                {bankSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Landmark className="w-4 h-4" />}
                {bankSaving ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </form>
          </div>
          {bankInfo.iban && (
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-3">
              <p className="text-emerald-400 text-xs font-semibold mb-1 flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5" /> IBAN enregistré</p>
              <p className="text-[#E5E5E5]/60 text-xs font-mono">{bankInfo.iban.replace(/(.{4})/g, '$1 ').trim()}</p>
            </div>
          )}
        </div>
      )}

      {/* ── Factures de services ── */}
      {activeTab === 'invoices-services' && (
        <div className="space-y-2" data-testid="driver-invoices-services">
          <h3 className="text-white font-semibold text-sm mb-1">Factures de services</h3>
          {servicesInvoices.length === 0 ? (
            <div className="text-center py-8"><FileText className="w-10 h-10 text-[#C9A24D]/20 mx-auto mb-2" /><p className="text-[#E5E5E5]/30 text-sm">Aucune facture de services</p></div>
          ) : servicesInvoices.map(inv => (
            <div key={inv.id} className="bg-[#1A1A1A] rounded-xl p-3 flex items-center justify-between">
              <div>
                <p className="text-[#C9A24D] text-xs font-mono">{inv.invoice_number}</p>
                <p className="text-[#E5E5E5]/50 text-[10px]">{fmtShortDate(inv.created_at)}</p>
                <div className="flex items-center gap-2 mt-0.5">{statusBadge(inv.status)}<span className="text-white text-sm font-semibold">{fmtCurrency(inv.amount)}</span></div>
              </div>
              {inv.file_url && (
                <a href={inv.file_url} target="_blank" rel="noopener noreferrer" className="text-[#C9A24D] flex items-center gap-1 text-xs hover:text-[#E6C77A]" data-testid={`download-service-invoice-${inv.id}`}>
                  <Download className="w-4 h-4" /> PDF
                </a>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Factures de commissions ── */}
      {activeTab === 'invoices-commissions' && (
        <div className="space-y-2" data-testid="driver-invoices-commissions">
          <h3 className="text-white font-semibold text-sm mb-1">Factures de commissions</h3>
          {commissionsInvoices.length === 0 ? (
            <div className="text-center py-8"><CreditCard className="w-10 h-10 text-[#C9A24D]/20 mx-auto mb-2" /><p className="text-[#E5E5E5]/30 text-sm">Aucune facture de commissions</p></div>
          ) : commissionsInvoices.map(inv => (
            <div key={inv.id} className="bg-[#1A1A1A] rounded-xl p-3 flex items-center justify-between">
              <div>
                <p className="text-orange-400 text-xs font-mono">{inv.invoice_number}</p>
                <p className="text-[#E5E5E5]/50 text-[10px]">{fmtShortDate(inv.created_at)}</p>
                <div className="flex items-center gap-2 mt-0.5">{statusBadge(inv.status)}<span className="text-white text-sm font-semibold">{fmtCurrency(inv.amount)}</span></div>
              </div>
              {inv.file_url && (
                <a href={inv.file_url} target="_blank" rel="noopener noreferrer" className="text-orange-400 flex items-center gap-1 text-xs hover:text-orange-300" data-testid={`download-commission-invoice-${inv.id}`}>
                  <Download className="w-4 h-4" /> PDF
                </a>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Transactions ── */}
      {activeTab === 'transactions' && (() => {
        const TX_PERIODS = [
          { key: '7', label: '7 jours' },
          { key: '30', label: '30 jours' },
          { key: '90', label: '90 jours' },
          { key: 'all', label: 'Tout' },
        ];
        const TX_TYPES = [
          { key: 'all', label: 'Tous' },
          { key: 'CREDIT_COURSE,MISSION_CREDIT', label: 'Courses' },
          { key: 'CREDIT_ATTENTE', label: 'Attente' },
          { key: 'DEBIT_COMMISSION', label: 'Commissions' },
          { key: 'PAYOUT_APPROVED,PAYOUT_REQUEST', label: 'Virements' },
        ];

        const now = new Date();
        const filtered = transactions.filter(tx => {
          // Period filter
          if (txPeriod !== 'all') {
            const days = parseInt(txPeriod);
            const cutoff = new Date(now.getTime() - days * 86400000);
            const txDate = tx.created_at ? new Date(tx.created_at) : null;
            if (!txDate || txDate < cutoff) return false;
          }
          // Type filter
          if (txType !== 'all') {
            const types = txType.split(',');
            if (!types.includes(tx.type)) return false;
          }
          return true;
        });

        return (
          <div className="space-y-3" data-testid="driver-wallet-transactions">
            {/* Period selector */}
            <div className="flex gap-1 bg-[#1A1A1A] rounded-lg p-1">
              {TX_PERIODS.map(p => (
                <button key={p.key} onClick={() => setTxPeriod(p.key)}
                  className={`flex-1 py-1.5 rounded text-[11px] font-medium transition-all
                    ${txPeriod === p.key ? 'bg-[#C9A24D] text-[#0B0B0B]' : 'text-[#E5E5E5]/50'}`}
                  data-testid={`tx-period-${p.key}`}>
                  {p.label}
                </button>
              ))}
            </div>
            {/* Type filter */}
            <div className="flex gap-1.5 overflow-x-auto no-scrollbar pb-0.5">
              {TX_TYPES.map(t => (
                <button key={t.key} onClick={() => setTxType(t.key)}
                  className={`flex-shrink-0 px-3 py-1 rounded-full text-[10px] font-medium border transition-all
                    ${txType === t.key ? 'bg-[#C9A24D]/20 border-[#C9A24D]/50 text-[#C9A24D]' : 'border-[#333] text-[#E5E5E5]/40'}`}
                  data-testid={`tx-type-${t.key}`}>
                  {t.label}
                </button>
              ))}
            </div>
            {/* List */}
            {filtered.length === 0 ? (
              <div className="text-center py-8"><List className="w-10 h-10 text-[#C9A24D]/20 mx-auto mb-2" /><p className="text-[#E5E5E5]/30 text-sm">Aucune transaction sur cette période</p></div>
            ) : filtered.map(tx => (
              <div key={tx.id} className="flex items-center justify-between bg-[#1A1A1A] rounded-xl px-3 py-2.5">
                <div>
                  {txTypeBadge(tx.type)}
                  <p className="text-[#E5E5E5]/20 text-[10px] mt-0.5">{tx.reference}</p>
                  <p className="text-[#E5E5E5]/20 text-[10px]">{fmtDate(tx.created_at)}</p>
                </div>
                {txAmountDisplay(tx)}
              </div>
            ))}
            <p className="text-center text-[#E5E5E5]/20 text-[10px]">{filtered.length} transaction{filtered.length > 1 ? 's' : ''}</p>
          </div>
        );
      })()}

      {/* ── Payouts ── */}
      {activeTab === 'payouts' && (
        <div className="space-y-2" data-testid="driver-wallet-payouts">
          {payoutRequests.length === 0 ? (
            <div className="text-center py-8"><ArrowDownCircle className="w-10 h-10 text-[#C9A24D]/20 mx-auto mb-2" /><p className="text-[#E5E5E5]/30 text-sm">Aucune demande de versement</p></div>
          ) : payoutRequests.map(p => (
            <div key={p.id} className="bg-[#1A1A1A] rounded-xl p-3">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">{statusBadge(p.status)}<span className="text-white font-semibold text-sm">{fmtCurrency(p.requested_amount)}</span></div>
                <span className="text-[#E5E5E5]/30 text-[10px]">{fmtShortDate(p.created_at)}</span>
              </div>
              {p.admin_comment && <p className="text-[#E5E5E5]/40 text-[10px] italic mb-1">"{p.admin_comment}"</p>}
              <div className="flex gap-2 mt-1">
                {p.missions_invoice_signed_url && (
                  <a href={p.missions_invoice_signed_url} target="_blank" rel="noopener noreferrer" className="text-[#C9A24D] text-[10px] flex items-center gap-1">
                    <FileText className="w-3 h-3" />Fact. services
                  </a>
                )}
                {p.commissions_invoice_signed_url && (
                  <a href={p.commissions_invoice_signed_url} target="_blank" rel="noopener noreferrer" className="text-orange-400 text-[10px] flex items-center gap-1">
                    <FileText className="w-3 h-3" />Fact. commissions
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
